// src/pages/PerformerDashboard/PerformerDashboard.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { jwtDecode } from "jwt-decode";

export default function PerformerDashboard() {
  const navigate = useNavigate();
  const [bandData, setBandData] = useState(null);
  const [loadingBand, setLoadingBand] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [loadingRes, setLoadingRes] = useState(true);
  const [activeTab, setActiveTab] = useState("reservations");
  const [genreError, setGenreError] = useState("");
  const [regionError, setRegionError] = useState("");
  const [setError, setSetError] = useState("");

  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");

  // --- Create form state ---
  const [showCreateForm, setShowCreateForm] = useState(false);
  const initialCreate = {
    slug: "",
    name: "",
    description: "",
    band_email: "",
    band_phone: "",
    location: "",
    membership_level: "basic",
    genre: "",
    video_link: "",
    band_address: "",
    profile_image: "",
    og_image_url: "",
    artist_type: "",
    instruments: "",
    additional_services: "",
    repertoire: "",
    gallery_images: "",
    audio_tracks: "",
    performance_regions: "",
    available_sets: "",
    facebook: "",
    instagram: "",
    tiktok: "",
    youtube: "",
  };

  const [createForm, setCreateForm] = useState(initialCreate);

  // --- Validation errors ---
  const [errors, setErrors] = useState({});

  const evaluatePasswordStrength = (password) => {
    if (!password) return "";
    if (password.length < 8) return "Heikko";
    if (/(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9])/.test(password)) return "Vahva";
    if (/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) return "Kohtalainen";
    return "Heikko";
  };

  // kenttä-määrittelyt
  const basicFields = [

    { id: "slug", 
      label: "Sivusi URL-tunniste",
      placeholder: "https://spiderlegsproductions.com/esiintyjä/?",
      type: "text", 
      info: "Käytä pieniä kirjaimia ja -merkkiä, ei ääkkösiä tai välilyöntejä. Esim: popduo2025" },

    { id: "name", 
      label: "Esiintyjän nimi",
      placeholder: "Sivun pääteksti, näkyy sivun yläreunassa..",
      type: "text" },

    { id: "description", 
      label: "Esiintyjän kuvaus", 
      placeholder: "Tämä teksti näkyy headerkuvan alapuolella..",
      type: "text", 
      isTextarea: true, 
      maxLength: 1100 },

    { id: "band_email", 
      label: "Esiintyjän sähköpostiosoite",
      placeholder: "Esiintyjän virallinen sähköposti..",
      type: "email", 
      required: true, 
      pattern: "[^\\s@]+@[^\\s@]+\\.[^\\s@]+", 
      title: "Syötä sähköpostiosoite muodossa nimi@domain.com", 
      info: "Syötä kelvollinen sähköpostiosoite" },

    { id: "contact_email", 
      label: "Yhteyshenkilön sähköpostiosoite",
      placeholder: "nimi@domain.com",
      type: "email", 
      required: true, 
      pattern: "[^\\s@]+@[^\\s@]+\\.[^\\s@]+", 
      title: "Syötä sähköpostiosoite muodossa nimi@domain.com", 
      info: "Syötä kelvollinen sähköpostiosoite" },

    { id: "profile_image",
      label: "Pääkuvan osoite",
      placeholder: "esim. https://example.com/kuvat/profiili.jpg",
      type: "url",
      info: "Suositeltu koko 1080x1080px. Jos et tiedä URLia tai haluat, että me lisäämme kuvan, ota yhteyttä."},

    { id: "band_address", 
      label: "Katuosoite",
      placeholder: "Anna esiintyjän osoite (esim. treenikämppä)", 
      type: "text" },

    { id: "audio_tracks", 
      label: "Ääninäyte (SoundCloud-linkki)", 
      placeholder: "https://soundcloud.com/spiderlegs-productions/?",
      type: "text" },

    { id: "og_image_url", 
      label: "Jakamiskuva",
      placeholder: "https://example.com/kuvat/profiili.jpg", 
      type: "url", 
      info: "Näkyy kun jaat profiilin somessa, paras kuvakoko: 1200x630px" },

    { id: "band_phone", 
      label: "Puhelinnumero", 
      type: "text",
      placeholder: "Anna puhelinnumero..", 
      info: "Käytä pieniä kirjaimia…" },

    { id: "location", 
      label: "Paikkakunta", 
      placeholder: "Esiintyjän paikkakunta..", 
      type: "text" },

    { id: "genre", 
      label: "Musiikkityyli(t)", 
      placeholder: "Nimeä genresi..", 
      type: "text" },

    { id: "performance_regions", 
      label: "Esiintymisalueet",
      placeholder: "Missä lääneissä haluatte esiintyä?", 
      type: "text" },

    { id: "artist_type", 
      label: "Esiintyjätyyppi", 
      placeholder: "Bändi, trubaduuri, DJ..", 
      type: "text" },

    { id: "available_sets", 
      label: "Saatavilla olevat setit", 
      placeholder: "2x45min, 3x45min jne..", 
      type: "text" },

    { id: "instruments", 
      label: "Soittimet", 
      placeholder: "Soittimet kokoonpanossa..", 
      type: "text" },
  
  ];

  const premiumFields = [
    { id: "video_link", label: "Linkki esittelyvideoon", type: "url" },
    { id: "band_address", label: "Katuosoite", type: "text" },
    { id: "profile_image", label: "Profiilikuva (URL)", type: "url" },
    { id: "og_image_url", label: "Jakamiskuva (URL)", type: "url" },
    { id: "additional_services", label: "Lisäpalvelut", type: "text" },
    { id: "repertoire", label: "Ohjelmisto", type: "text" },
    { id: "gallery_images", label: "Galleria (JSON)", type: "text" },
    { id: "audio_tracks", label: "Ääninäytteet (JSON)", type: "text" },
    { id: "facebook", label: "Facebook-linkki", type: "url" },
    { id: "instagram", label: "Instagram-linkki", type: "url" },
    { id: "tiktok", label: "TikTok-linkki", type: "url" },
    { id: "youtube", label: "YouTube-linkki", type: "url" },
  ];


  

  const formatDateFI = (iso) =>
    new Date(iso).toLocaleString("fi-FI", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;
        let decoded;
        try {
            decoded = jwtDecode(token);
        } catch {
            return;
        }
        const userId = decoded.user_id;
        axios.post("https://spiderlegsproductions.com/api/get_performer_user.php", { userId })

            .then(r => {
            if (r.data.success) setUserData(r.data.user);
            })
            .finally(() => setLoadingUser(false));
        }, []);
      

useEffect(() => {
    if (!bandData) return;
  
    // GENRE
    const genreCount = (bandData.genre || "")
      .split(",").map(s => s.trim()).filter(s => s).length;
  
    if (bandData.membership_level === "basic" && genreCount > 1) {
      setGenreError("Basic-profiililla voit lisätä vain 1 musiikkityylin.");
    } else if (genreCount > 4) {
      setGenreError("Premium-profiililla voi enintään 4 tyyliä.");
    } else {
      setGenreError("");
    }
  
    // SETIT
    if (bandData.available_sets !== undefined && bandData.available_sets !== null) {
      const setsCount = bandData.available_sets
        .split(",").map(s => s.trim()).filter(s => s).length;
  
      if (bandData.membership_level === "basic" && setsCount > 1) {
        setSetError("Basic-profiililla vain 1 setti.");
      } else {
        setSetError("");
      }
    }
  
    // ALUEET
    const regionCount = (bandData.performance_regions || "")
      .split(",").map(s => s.trim()).filter(s => s).length;
  
    if (bandData.membership_level === "basic" && regionCount > 1) {
      setRegionError("Basic-profiililla vain 1 alue.");
    } else if (regionCount > 4) {
      setRegionError("Premium hasta 4 aluetta.");
    } else {
      setRegionError("");
    }
  
  }, [bandData]);
  
      
  // fetch data
  useEffect(() => {
    const token = localStorage.getItem("token");
    const pt = localStorage.getItem("profileType");
    if (!token) return navigate("/kirjaudu");
    if (pt !== "performer") return navigate("/choose-profile");

    const slug = localStorage.getItem("performerSlug");

    
    if (!slug) {
      setLoadingBand(false);
      setLoadingRes(false);
      return;
    }
    (async () => {
        try {
          const { data: bandRes } = await axios.get(
            `https://spiderlegsproductions.com/api/get_band.php?slug=${slug}`
          );
          if (!bandRes.success) {
            toast.error(bandRes.error || "Profiilitietoja ei löytynyt.");
            return;
          }
          setBandData(bandRes.band);
    
          const { data: resRes } = await axios.post(
            "https://spiderlegsproductions.com/api/get_band_reservations.php",
            { bandId: bandRes.band.id }
          );
          if (resRes.success) {
            setReservations(resRes.reservations);
          } else {
            toast.error(resRes.error || "Varauksia ei löytynyt.");
          }
        } catch (e) {
          console.error(e);
          toast.error("Tietojen haku epäonnistui.");
        } finally {
          setLoadingBand(false);
          setLoadingRes(false);
        }
      })();
    }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("profileType");
    navigate("/kirjaudu");
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordStrength(evaluatePasswordStrength(value));
  };

  const handleSaveUserData = async () => {
    if (!userData) return;
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/update_rental_user.php",
        {
          userId: userData.id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          address: userData.address,
          postal_code: userData.postal_code,
          city: userData.city,
        }
      );
      data.success
        ? toast.success(data.message)
        : toast.error(data.error || "Tallennus epäonnistui");
    } catch (e) {
      toast.error("Virhe tallennettaessa");
      console.error(e);
    }
  };
  
  const handleDeleteAccount = async () => {
    if (!userData) return;
    const confirmDelete = window.confirm(
      "Haluatko varmasti poistaa tilisi? Kaikki tietosi poistetaan pysyvästi."
    );
    if (!confirmDelete) return;
  
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/delete_rental_user.php",
        { userId: userData.id }
      );
      if (data.success) {
        toast.success("Tili poistettu");
        handleLogout();
      } else {
        toast.error(data.error || "Poisto epäonnistui");
      }
    } catch (e) {
      toast.error("Palvelinvirhe poistossa");
      console.error(e);
    }
  };
  
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Täytä molemmat kentät.");
      return;
    }
  
    if (newPassword.length < 8) {
      toast.error("Uuden salasanan tulee olla vähintään 8 merkkiä.");
      return;
    }
  
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/change_password.php",
        {
          userId: userData.id,
          currentPassword,
          newPassword,
        }
      );
      if (data.success) {
        toast.success(data.message);
        setCurrentPassword("");
        setNewPassword("");
      } else {
        toast.error(data.error || "Virhe salasanan vaihdossa.");
      }
    } catch (e) {
      toast.error("Palvelinvirhe salasanan vaihdossa.");
      console.error(e);
    }
  };

  const handleUpgradeToPremium = async () => {
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/change_membership_level.php",
        {
          bandId: bandData.id,
          newLevel: "premium",
        }
      );
      if (data.success) {
        toast.success("Profiili päivitetty Premium-tasolle");
        window.location.reload();
      } else {
        toast.error(data.error || "Päivitys epäonnistui");
      }
    } catch (e) {
      toast.error("Virhe päivitettäessä jäsenyystasoa");
      console.error(e);
    }
  };
  
  const handleDowngradeToBasic = () => {
    toast.info(
      ({ closeToast }) => (
        <div>
          <p>Haluatko varmasti palata Basic-tasolle?<br />Osa Premium-ominaisuuksista poistuu näkyvistä.</p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                closeToast();
                try {
                  const { data } = await axios.post(
                    "https://spiderlegsproductions.com/api/change_membership_level.php",
                    { bandId: bandData.id, newLevel: "basic" }
                  );
                  if (data.success) {
                    toast.success("Profiili muutettu Basic-tasolle");
                    window.location.reload();
                  } else {
                    toast.error(data.error || "Päivitys epäonnistui");
                  }
                } catch (e) {
                  toast.error("Virhe päivitettäessä jäsenyystasoa");
                  console.error(e);
                }
              }}
              className="bg-green-600 text-white px-4 py-1 rounded-full text-sm"
            >
              Vahvista
            </button>
            <button
              onClick={closeToast}
              className="bg-gray-300 text-black px-4 py-1 rounded-full text-sm"
            >
              Peruuta
            </button>
          </div>
        </div>
      ),
      { autoClose: false }
    );
  };
  
  
  const handleCancelPerformerProfile = () => {
    toast.info(
      ({ closeToast }) => (
        <div>
          <p>
            Haluatko varmasti lopettaa esiintyjätilauksen?<br />
            Profiilisi pysyy näkyvissä maksetun kauden loppuun asti, mutta sen jälkeen se poistuu.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                closeToast();
                try {
                  const { data } = await axios.post(
                    "https://spiderlegsproductions.com/api/cancel_performer_subscription.php",
                    { bandId: bandData.id }
                  );
                  if (data.success) {
                    toast.success("Tilaus lopetettu. Profiili poistuu kauden lopussa.");
                    window.location.reload();
                  } else {
                    toast.error(data.error || "Tilauksen lopetus epäonnistui");
                  }
                } catch (e) {
                  toast.error("Virhe tilauksen lopetuksessa");
                  console.error(e);
                }
              }}
              className="bg-red-600 text-white px-4 py-1 rounded-full text-sm"
            >
              Vahvista
            </button>
            <button
              onClick={closeToast}
              className="bg-gray-300 text-black px-4 py-1 rounded-full text-sm"
            >
              Peruuta
            </button>
          </div>
        </div>
      ),
      { autoClose: false }
    );
  };
  
  const handlePublishProfile = async (id) => {
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/publish_band.php",
        { id }
      );
      if (data.success) {
        toast.success("Profiili julkaistu!");
        window.location.reload();
      } else {
        toast.error(data.error || "Julkaisu epäonnistui.");
      }
    } catch (e) {
      toast.error("Virhe julkaistaessa profiilia.");
      console.error(e);
    }
  };
  
  
  // Input changes
  const handleCreateChange = (e) => {
    const { id, value } = e.target;
    if (id === "slug") {
      const sanitized = value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      setCreateForm((f) => ({ ...f, [id]: sanitized }));
    } else {
      setCreateForm((f) => ({ ...f, [id]: value }));
    }
    // clear error on change
    setErrors(errs => ({ ...errs, [id]: undefined }));
  };

  // submit creation
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

     // ★ genre-validointi
  const genreCount = (createForm.genre || "")
    .split(",").map(s=>s.trim()).filter(s=>s).length;
  if (createForm.membership_level==="basic" && genreCount>1) {
    newErrors.genre = "Basic-profiilissa saa olla vain 1 musiikkityyli.";
  }
  if (createForm.membership_level==="premium" && genreCount>4) {
    newErrors.genre = "Premium-profiilissa saa olla enintään 4 musiikkityyliä.";
  }

  // ★ sähköposti (ole jo valmiina)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!createForm.band_email) {
    newErrors.band_email = "Sähköpostiosoite vaaditaan.";
  } else if (!emailRegex.test(createForm.band_email)) {
    newErrors.band_email = "Syötä kelvollinen sähköpostiosoite.";
  }

  // jos virheitä, näytä ja lopeta
  if (Object.keys(newErrors).length) {
    setErrors(newErrors);
    return;
  }

    try {
      const params = new URLSearchParams();
      params.append("membership_level", createForm.membership_level);
      basicFields.forEach(f => params.append(f.id, createForm[f.id] || ""));
      if (createForm.membership_level === "premium") {
        premiumFields.forEach(f => params.append(f.id, createForm[f.id] || ""));
      }
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/create_band.php",
        params.toString(),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (data.success) {
        localStorage.setItem("performerSlug", createForm.slug);
        toast.success("Profiili luotu!");
        setShowCreateForm(false);
        window.location.reload();
      } else {
        toast.error(data.error || "Profiilin luonti epäonnistui.");
      }
    } catch {
      toast.error("Palvelinvirhe profiilia luodessa.");
    }
  };

  // save profile
const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      const allowedFields = [
        "id", "name", "description", "band_email", "contact_email",
        "band_phone", "location", "available_sets",
        "genre", "artist_type", "instruments", "performance_regions",
        "profile_image", "band_address", "audio_tracks", "og_image_url"
      ];
  
      // Premium-kentät lisätään vain jos taso on premium
      if (bandData?.membership_level === "premium") {
        allowedFields.push(
          "genre",
          "video_link",
          "band_address",
          "profile_image",
          "og_image_url",
          "artist_type",
          "instruments",
          "additional_services",
          "repertoire",
          "gallery_images",
          "audio_tracks",
          "performance_regions",
          "available_sets",
          "facebook",
          "instagram",
          "tiktok",
          "youtube"
        );
      }
  
      const payload = {};
      allowedFields.forEach((key) => {
        if (key === "id" || bandData?.[key]) {
          payload[key] = bandData[key] || "";
        }
      });
  
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/update_band_profile.php",
        payload
      );
  
      if (data.success) {
        toast.success("Profiili päivitetty");
        window.scrollTo({ top: 0, behavior: "smooth" }); // vieritä ylös
      } else {
        toast.error(data.error || "Päivitys epäonnistui");
      }
    } catch {
      toast.error("Palvelinvirhe profiilin päivityksessä.");
    }
  };
  
  const editableFields = [
    ...basicFields,
    ...(bandData?.membership_level === "premium" ? premiumFields : [])
  ];
  

  const now = new Date();
  const upcoming = reservations.filter(r => new Date(r.end) >= now);
  const past = reservations.filter(r => new Date(r.end) < now);
  const slug = localStorage.getItem("performerSlug");

  return (
    <div className="pt-24 pb-12 px-4 min-h-screen flex
        xxxsm:flex-col xxsm:flex-col s24:flex-col xsm:flex-col sm:flex-col
        md:flex-col mdd:flex-col lg:flex-row xl:flex-row 2xl:flex-row
        bg-linkcol dark:bg-violet dark:bg-opacity-75
        font-customFont1 text-darkest dark:text-primary dark:text-shadow-lg">

    <aside className="mb-8
    xxxsm:w-full xxsm:w-full s24:w-full xsm:w-full sm:w-full
    md:w-full mdd:w-1/3 lg:w-1/4 xl:w-1/4 2xl:w-1/4
    bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md p-4
    text-center lg:text-left">

    <h2 className="text-4xl font-bold mb-4 font-customFont2 dark:text-shadow-xxl xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left">Valikko</h2>

    <nav className="flex flex-col space-y-4 text-center">
    <button
        onClick={() => setActiveTab("reservations")}
        className={`transition-all duration-300 ease-in-out hover:text-accent hover:translate-x-1 hover:drop-shadow-md text-left ${
            activeTab === "reservations" ? "font-bold underline text-accent" : ""
        }`}
        >
        Vuokraukset
        </button>
        <button
        onClick={() => setActiveTab("profile")}
        className={`transition-all duration-300 ease-in-out hover:text-accent hover:translate-x-1 hover:drop-shadow-md text-left ${
            activeTab === "profile" ? "font-bold underline text-accent" : ""
        }`}
        >
        Esiintyjäprofiili
        </button>
        <button
        onClick={() => setActiveTab("settings")}
        className={`transition-all duration-300 ease-in-out hover:text-accent hover:translate-x-1 hover:drop-shadow-md text-left ${
            activeTab === "settings" ? "font-bold underline text-accent" : ""
        }`}
        >
        Tiliasetukset
        </button>

    </nav>

    <div className="mt-10">
        <ResponsiveButton onClick={handleLogout} variant="removefromcart">
        Kirjaudu ulos
        </ResponsiveButton>
    </div>
    </aside>


      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 mx-4 rounded-3xl dark:bg-violet bg-primary">
        {/* CREATE */}
        {!slug && !loadingBand && !loadingRes ? (
          <div>
            {!showCreateForm ? (
              <ResponsiveButton
                onClick={() => setShowCreateForm(true)}
                variant="addtocart"
              >
                Luo esiintyjäprofiili
              </ResponsiveButton>
            ) : (
              <form
                onSubmit={handleCreateSubmit}
                className="bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md space-y-4 p-4"
              >
                <h2 className="text-2xl font-bold mb-4">
                  Uusi esiintyjäprofiili
                </h2>

                {/* membership */}
                <div>
                  <label htmlFor="membership_level" className="block font-semibold mb-1">
                    Tilaustaso
                  </label>
                  <select
                    id="membership_level"
                    value={createForm.membership_level}
                    onChange={handleCreateChange}
                    className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg pl-5"
                  >
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>

                {/* basic */}
                {basicFields.map(({ id, label, type, pattern, title, isTextarea, maxLength, info, placeholder }) => (
                <div key={id}>
                    <label htmlFor={id} className="block font-semibold mb-1">
                    {label}
                    </label>
                    {isTextarea ? (
                    <textarea
                        id={id}
                        value={createForm[id]}
                        onChange={handleCreateChange}
                        maxLength={maxLength}
                        placeholder={placeholder || label}   // <-- placeholder
                        className="w-full p-3 rounded-xl …"
                        rows={4}
                    />
                    ) : (
                    <input
                        id={id}
                        type={type}
                        pattern={pattern}
                        title={title}
                        value={createForm[id]}
                        onChange={handleCreateChange}
                        placeholder={placeholder || label}   // <-- placeholder
                        className={`w-full p-3 rounded-full … ${errors[id] ? "border-red-500" : ""}`}
                    />
                    )}
                    {info && <p className="text-sm italic mt-1">{info}</p>}
                    {errors[id] && <p className="text-sm text-red-600 mt-1">{errors[id]}</p>}
                    {id === "description" && (
                    <p className={`text-sm italic mt-1 …`}>
                        {createForm.description.length}/1100 merkkiä
                    </p>
                    )}
                </div>
                ))}

                {/* premium */}
                {createForm.membership_level === "premium" &&
                  premiumFields.map(({ id, label, type }) => (
                    <div key={id}>
                      <label htmlFor={id} className="block font-semibold mb-1">
                        {label}
                      </label>
                      <input
                        id={id}
                        type={type}
                        value={createForm[id]}
                        onChange={handleCreateChange}
                        placeholder={label}
                        className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                      />
                    </div>
                    
                  ))}

                <div className="flex space-x-2">
                  <ResponsiveButton type="submit" variant="addtocart">
                    Tallenna profiili
                  </ResponsiveButton>
                  <ResponsiveButton
                    type="button"
                    variant="secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Peruuta
                  </ResponsiveButton>
                </div>
              </form>
            )}
          </div>
        ) : slug && (loadingBand || loadingRes) ? (
          <p>Ladataan…</p>
        ) : slug && !bandData ? (
          <p className="text-red-600">Profiilitietoja ei löytynyt.</p>
        ) : activeTab === "reservations" ? (
          <>
            <h1 className="text-4xl font-bold mb-4 font-customFont2 dark:text-shadow-xxl">
            Tervetuloa, {userData?.name}
            </h1>
            <h2 className="text-3xl font-bold mb-2 font-customFont2 dark:text-shadow-lg xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left">Aktiiviset varaukset</h2>
            {upcoming.length ? (
              upcoming.map(r => (
                <div key={r.varausID} className="p-4 bg-white dark:bg-darkest rounded-xl shadow mb-4">
                  <h3 className="font-bold">{formatDateFI(r.start)} – {formatDateFI(r.end)}</h3>
                  <p><strong>Status:</strong> {r.status === "vahvistettu" ? "✅ Vahvistettu" : "⏳ Odottaa"}</p>
                  <p>Nimi: {r.name}</p>
                  <p>Sähköposti: {r.sahkoposti}</p>
                </div>
              ))
            ) : <p className="text-purple-400">Ei aktiivisia varauksia.</p>}
            <h2 className="text-3xl font-bold mb-2 font-customFont2 dark:text-shadow-lg xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left mt-8 ">Menneet varaukset</h2>
            {past.length ? (
              past.map(r => (
                <div key={r.varausID} className="p-4 bg-gray-200 dark:bg-gray-800 rounded-xl shadow mb-4 opacity-70">
                  <h3 className="font-bold">{formatDateFI(r.start)} – {formatDateFI(r.end)}</h3>
                  <p>Nimi: {r.name}</p>
                  <p>Sähköposti: {r.sahkoposti}</p>
                </div>
              ))
            ) : <p className="text-purple-400">Ei menneitä varauksia.</p>}
          </>
             ) : activeTab === "profile" ? (
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <h2 className="text-4xl font-bold mb-4 font-customFont2 dark:text-shadow-xxl">Muokkaa profiilia</h2>
      
                  {editableFields
                    .filter(({ id }) => {
                        if (bandData?.membership_level !== "premium") {
                        const trulyPremiumOnly = [
                            "video_link",
                            "additional_services", "repertoire",
                            "gallery_images",
                            "facebook", "instagram", "tiktok", "youtube"
                        ];
                        return !trulyPremiumOnly.includes(id);
                        }
                        return true;
                    })
                    .map(({ id, label, type, isTextarea, maxLength, info, placeholder }) => (
                        <div key={id}>
                        <label htmlFor={id} className="block font-semibold mb-1">
                            {label}
                        </label>

                        {isTextarea ? (
                            <>
                            <textarea
                                id={id}
                                value={bandData?.[id] || ""}
                                onChange={(e) =>
                                setBandData((b) => ({ ...b, [id]: e.target.value }))
                                }
                                maxLength={maxLength}
                                placeholder={placeholder || label}
                                className="w-full p-3 rounded-xl bg-white dark:bg-darkest placeholder-gray-500 dark:placeholder-gray-400 border-none focus:outline-none text-lg font-customFont1 pl-5"
                                rows={4}
                            />
                            {maxLength && (
                                <p
                                className={`text-sm italic mt-1 ${
                                    (bandData?.[id]?.length || 0) > maxLength
                                    ? "text-red-500"
                                    : "text-gray-600 dark:text-gray-300"
                                }`}
                                >
                                {(bandData?.[id]?.length || 0)}/{maxLength} merkkiä
                                </p>
                            )}
                            </>
                        ) : (
                            <input
                            id={id}
                            type={type}
                            value={bandData?.[id] || ""}
                            onChange={(e) =>
                                setBandData((b) => ({ ...b, [id]: e.target.value }))
                            }
                            placeholder={placeholder || label}
                            className="w-full p-3 rounded-full bg-white dark:bg-darkest placeholder-gray-500 dark:placeholder-gray-400 border-none focus:outline-none text-lg font-customFont1 pl-5"
                            />
                        )}

                        {/* 🔹 Tämä näyttää info-tekstin */}
                        {info && (
                            <p className="text-sm italic text-gray-600 dark:text-gray-300 mt-1">
                            {info}
                            </p>
                        )}

                        {id === "genre" && genreError && (
                            <p className="text-sm text-red-500 mt-1">{genreError}</p>
                        )}
                        {id === "performance_regions" && regionError && (
                            <p className="text-sm text-red-500 mt-1">{regionError}</p>
                        )}
                        {id === "available_sets" && setError && (
                            <p className="text-sm text-red-500 mt-1">{setError}</p>
                        )}
                        </div>
                    ))}

                    <ResponsiveButton
                    type="submit"
                    variant="addtocart"
                    disabled={!!genreError || !!regionError || !!setError}
                    >
                    Tallenna profiili
                    </ResponsiveButton>

                    {!bandData?.is_published && (
                    <ResponsiveButton
                        onClick={() => handlePublishProfile(bandData.id)}
                        variant="addtocart"
                    >
                        Julkaise profiili
                    </ResponsiveButton>
                    )}


<ResponsiveButton
onClick={() =>
  window.open(`https://spiderlegsproductions.com/esiintyja/${bandData.slug}?preview=true`, "_blank")
}
variant="addtocart"
>
Esikatsele profiilia
</ResponsiveButton>




                </form>
             ) : activeTab === "settings" && loadingUser ? (
                <p>Ladataan käyttäjätietoja...</p>
              ) : (
                <div>
                <h2 className="text-4xl font-bold mb-4 font-customFont2 dark:text-shadow-xxl">Käyttäjän tiliasetukset</h2>
                <p>Taso: {bandData?.membership_level || "-"}</p>
              
                {userData && (
  <>
    <p className="mt-2">Käyttäjän nimi: {userData.name}</p>
    <p className="mb-4">Sähköposti: {userData.email}</p>

    {/* Nimi */}
    <label htmlFor="name" className="block font-semibold mb-1">Nimi</label>
    <input
      id="name"
      type="text"
      value={userData.name || ""}
      onChange={e => setUserData({ ...userData, name: e.target.value })}
      className="w-full p-3 mb-4 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
      placeholder="Nimesi"
    />

    {/* Puhelin */}
    <label htmlFor="phone" className="block font-semibold mb-1">Puhelinnumero</label>
    <input
    id="phone"
    type="text"
    value={userData.phone || ""}
    onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
    className="w-full p-3 mb-4 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
    placeholder="Puhelinnumerosi"
    />

    {/* Katuosoite */}
    <label htmlFor="address" className="block font-semibold mb-1">Katuosoite</label>
    <input
    id="address"
    type="text"
    value={userData.address || ""}
    onChange={(e) => setUserData({ ...userData, address: e.target.value })}
    className="w-full p-3 mb-4 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
    placeholder="Katuosoitteesi"
    />

    {/* Postinumero */}
    <label htmlFor="postal_code" className="block font-semibold mb-1">Postinumero</label>
    <input
    id="postal_code"
    type="text"
    value={userData.postal_code || ""}
    onChange={(e) => setUserData({ ...userData, postal_code: e.target.value })}
    className="w-full p-3 mb-4 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
    placeholder="Postinumerosi"
    />

    {/* Kaupunki */}
    <label htmlFor="city" className="block font-semibold mb-1">Kaupunki</label>
    <input
    id="city"
    type="text"
    value={userData.city || ""}
    onChange={(e) => setUserData({ ...userData, city: e.target.value })}
    className="w-full p-3 mb-4 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
    placeholder="Kaupunki"
    />


    <ResponsiveButton onClick={handleSaveUserData}
    variant="addtocart"
    >
      Tallenna tiedot
    </ResponsiveButton>
    
    <hr className="mb-6 mt-10 border-t-2 border-accent shadow-lg w-full" />

    {/* Tilauksen hallinta */}
    {bandData?.membership_level === "basic" && (
    <div className="mt-6 p-4 bg-blue-100 dark:bg-blue-800 rounded-xl">
        <p className="font-semibold mb-2">
        Profiilisi on Basic-tasolla. Päivittämällä Premiumiin saat käyttöösi lisäominaisuuksia, kuten useampia musiikkityylejä, video- ja kuvalinkkejä sekä some-profiilien esittelyn.
        </p>
        <ResponsiveButton
        onClick={handleUpgradeToPremium}
        variant="addtocart"
        >
        Päivitä Premiumiin
        </ResponsiveButton>
    </div>
    )}

    {bandData?.membership_level === "premium" && (
      <div className="mt-6 p-4 rounded-xl space-y-4 dark:bg-violet bg-primary">
        <div>
        <h2 className="text-3xl font-bold mb-2 font-customFont2 dark:text-shadow-lg xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left">Esiintyjäprofiilin muutokset</h2>
            <p className="font-semibold mb-6">
                Nykyinen tilauksesi on Premium. Voit vaihtaa takaisin Basic-tasolle, jolloin osa ominaisuuksista poistuu käytöstä.
            </p>
            <ResponsiveButton
                onClick={handleDowngradeToBasic}
                variant="addtocart"
            >
                Palaa Basic-jäsenyyteen
            </ResponsiveButton>
            </div>
        <div>
        <p className="font-semibold mb-6">
            Voit lopettaa esiintyjätilauksesi tästä kokonaan. Profiilisi pysyy näkyvissä maksetun kauden loppuun asti, ja sen jälkeen tilisi muuttuu tavalliseksi vuokraajatiliksi – voit edelleen tehdä laitevarauksia normaalisti.
        </p>

          <ResponsiveButton
            onClick={handleCancelPerformerProfile}
            variant="removefromcart"
            >
            Lopeta esiintyjätilaus
          </ResponsiveButton>
        </div>
      </div>
    )}

    <hr className="my-6 border-t-2 border-accent shadow-lg w-full" />

    {/* Tilin poisto */}
    <div className="mt-6 p-4 rounded-xl dark:bg-violet bg-primary">
    <h2 className="text-3xl font-bold mb-2 font-customFont2 dark:text-shadow-lg xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left">Tilin poistaminen</h2>

    <p className="font-semibold mb-6">
        Tilin poistaminen on lopullinen toimenpide. Kaikki henkilö- ja varaus­tietosi poistetaan pysyvästi, eikä niitä voida palauttaa myöhemmin.
    </p>

      <ResponsiveButton
        onClick={handleDeleteAccount}
        variant="removefromcart"
      >
        Poista tili
      </ResponsiveButton>
    </div>

    <hr className="mb-6 mt-8 border-t-2 border-accent shadow-lg w-full" />

    {/* Salasanan vaihto */}
    <div className="mt-6 space-y-4 p-4">
    <h2 className="text-3xl font-bold mb-2 font-customFont2 dark:text-shadow-lg xxxsm:text-left xxsm:text-left s24:text-left sm:text-left md:text-left mdd:text-left lg:text-left xl:text-left 2xl:text-left">Salasana</h2>
    <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
        Voit vaihtaa salasanasi kirjoittamalla nykyisen ja uuden salasanan alle. Uuden salasanan tulee olla vähintään 8 merkkiä pitkä.
        </p>
        <input
  type="password"
  placeholder="Uusi salasana (väh. 8 merkkiä)"
  value={newPassword}
  onChange={handlePasswordChange}
  className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg"
/>
<p className={`mt-1 text-sm ${
  passwordStrength === "Vahva"
    ? "text-green-500"
    : passwordStrength === "Kohtalainen"
    ? "text-yellow-500"
    : "text-red-500"
}`}>
  Vahvuus: {passwordStrength}
</p>


      <ResponsiveButton onClick={handleChangePassword}
      variant="addtocart"
      >
        Vaihda salasana
      </ResponsiveButton>
    </div>
  </>
)}

              </div>
              
              
              
        )}
      </main>
    </div>
  );
}
